import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { CardContent, CardHeader } from "../../base/card/card";
import { Skeleton } from "../../base/skeleton/skeleton";
import { EntityDetailTitle } from "../entity-detail-title/entity-detail-title";
import { formatDate } from "../data-table/utils";
import { ChatPartCard, ChatPartNotice } from "./chat-part-shell";
/**
 * The frame and the placeholders every embedded record view shares (plan 016).
 *
 * They live in their own module rather than beside any one view because all
 * four views draw them and the block that dispatches between the views draws
 * them too — putting them in the dispatcher would make each view import its own
 * parent.
 */ /** Placeholder while the ids stream, the process loads, or the lookups warm up. */ export function ChatEntityViewSkeleton() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs(ChatPartCard, {
        role: "status",
        label: t("chat.entityView.loading"),
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                className: "pb-2",
                children: /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-5 w-56"
                })
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "flex flex-col gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-3 w-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-3 w-5/6"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-3 w-2/3"
                    })
                ]
            })
        ]
    });
}
/**
 * Whatever a view shows when there is no record to show yet, or ever.
 *
 * Every view answers the three non-ready states identically, so they answer
 * them HERE — a second copy would be a second chance to get one wrong, and the
 * one that matters most (never claiming a record is gone while its fetch is
 * still in flight) is the easiest to get wrong twice.
 */ export function ChatEntityViewStateNotice({ state }) {
    const { t } = useTranslation();
    switch(state.status){
        case "pending":
            return /*#__PURE__*/ _jsx(ChatEntityViewSkeleton, {});
        case "unavailable":
            return /*#__PURE__*/ _jsx(ChatPartNotice, {
                message: t("chat.entityView.unavailable")
            });
        case "failed":
            return /*#__PURE__*/ _jsx(ChatPartNotice, {
                message: t("chat.entityView.loadFailed"),
                onRetry: state.retry
            });
        default:
            {
                // Same guard the view switches carry: a fourth non-ready state would
                // otherwise draw nothing, because `ReactNode` includes `undefined`.
                const unhandled = state;
                throw new Error(`unhandled view state: ${JSON.stringify(unhandled)}`);
            }
    }
}
/**
 * The frame every process-shaped view draws in: the record's current name, when
 * it was last changed, and the way back to the process page that owns it.
 *
 * Process-shaped by name because it is process-shaped in fact — `processId` is
 * required and the link goes to the process route. Shared so a decision table, a
 * step detail and a whole flow are the same object in the transcript, and so the
 * "this is live, unlike the prose beside it" label cannot end up on one and not
 * the others.
 */ export function ChatProcessRecordCard({ data, name, updatedAt, processId, slug, actions, children }) {
    const { t, i18n } = useTranslation();
    const { ProcessLink } = data;
    return /*#__PURE__*/ _jsxs(ChatPartCard, {
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                className: "gap-1 pb-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(EntityDetailTitle, {
                                name: name,
                                headingLevel: 3
                            }),
                            actions
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            t("chat.entityView.current"),
                            " ·",
                            " ",
                            t("chat.entityView.updatedAt", {
                                // The helper answers `-` rather than interpolating "undefined".
                                date: formatDate(updatedAt, i18n.language)
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(CardContent, {
                className: "flex flex-col gap-3",
                children: [
                    children,
                    slug && /*#__PURE__*/ _jsxs(ProcessLink, {
                        processId: processId,
                        slug: slug,
                        className: "inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline",
                        children: [
                            t("chat.entityView.openProcess"),
                            /*#__PURE__*/ _jsx(ArrowUpRight, {
                                className: "h-3.5 w-3.5 shrink-0",
                                "aria-hidden": true
                            })
                        ]
                    })
                ]
            })
        ]
    });
}

//# sourceMappingURL=chat-entity-view-shell.js.map